import { createWmtsLayer, getWmtsOptions } from './wmts';
import { createWmsLayer } from './wms';
import { createWfsLayer } from './wfs';
import { createOgctLayer } from './ogcapi';
import { NEDGLOBE_MAP_RESOLUTION, CONTENT_TYPE_ID, NEDGLOBE_VIEWER, SORT_ORDER, FEEDBACKS_GEOMETRY_TYPE_CODES } from '../../../constants';
import { stringNotEmpty } from '../../utilities';
import { useOgcWebServiceDirectly, GetServiceUrl } from '../../../services/map/layer/filter';
import { getStyleSettings, preloadSvgImages } from './styles';

export const extendThemeLayer = async function (store, layer) {
    const { layerData, type: layerType } = layer;
    const extendedLayer = { ...layerData };

    // Copy default label attributes and store them separately so we can rest defaults later.
    let defaultAttributes = [];
    if (layerData?.informationAttributes) {
        defaultAttributes = JSON.parse(JSON.stringify(layerData.informationAttributes.filter(i => i.isTextAttribute)));
        getLabelsFromUsermap(store, layer, layerType, extendedLayer);
    }

    extendedLayer.defaultAttributes = defaultAttributes;
    const rotationAtt = layerData.geometryType?.includes('P') ? layerData.rotationAttribute : undefined;

    await preloadSvgImages(store, layerData.styles);
    extendedLayer.computedStyles = await getStyleSettings(layerData.styles, rotationAtt);
    if (extendedLayer.computedStyles.length > 0) {
        extendedLayer.computedStyles[0].settings.default.labels = getLabels(layerData.informationAttributes);
    }

    if (layerType === 'feedback') {
        const feedBackTheme = store.getters.getFeedbackById(layerData.feedbackId);
        const feedbackfields = feedBackTheme.data.feedbackType.feedbackFields;
        extendedLayer.feedbackFields = feedbackfields;
        extendedLayer.userRights = { edit: feedBackTheme.data.userEdit, remove: feedBackTheme.data.userRemove };
        extendedLayer.layerName = extendedLayer.layerName.toLowerCase();
        extendedLayer.geometryColumn = 'geolocation';
        const isWeb = store.getters.isWeb;
        if (isWeb && feedBackTheme.data.feedbackType.contentTypeId === CONTENT_TYPE_ID.FEEDBACK && feedBackTheme.data.feedbackType.useInWebTheme) {
            // only use clusters if web feedback layer consists entirely of points
            extendedLayer.useCluster = feedBackTheme.data.geometryType === FEEDBACKS_GEOMETRY_TYPE_CODES.POINT;
            extendedLayer.minResolutionCluster = extendedLayer.minResolution;
            extendedLayer.maxResolutionCluster = extendedLayer.maxResolution;
            // webmeldingen are always visible and thereby selectable, the min and max resolutions are ignored
            extendedLayer.minResolution = undefined;
            extendedLayer.maxResolution = undefined;
        }
        if (feedBackTheme.data.feedbackType.contentTypeId === CONTENT_TYPE_ID.MEASUREMENT) {
            extendedLayer.computedStyles.forEach(item => {
                for (const featureStyleType in item.settings) {
                    item.settings[featureStyleType].showLabel = true;
                }
            });
        }
    } else if (!extendedLayer.geometryColumn) {
        extendedLayer.geometryColumn = 'geometrie';
    }
    extendedLayer.useOgcWebServiceDirectly = useOgcWebServiceDirectly(layerData);
    extendedLayer.getCombinedOlId = function () {
        if (!extendedLayer.themeId) {
            return extendedLayer.id;
        }
        return `${extendedLayer.themeId}-${extendedLayer.id}`;
    };

    extendedLayer.resolution = NEDGLOBE_MAP_RESOLUTION;
    // NEDGLOBETODO move to wmts.js
    if (extendedLayer.serviceType === 'WMTS' && !extendedLayer.olLayerOptions) {
        extendedLayer.olLayerOptions = await getWmtsOptions(store, extendedLayer.id, extendedLayer.layerName, extendedLayer.tileMatrixSet);
    }

    extendedLayer.hasGraphicalLayerAndStyleTextAttribute = extendedLayer.graphicalLayerName?.length > 0 &&
        (stringNotEmpty(extendedLayer.computedStyles[0]?.settings[NEDGLOBE_VIEWER.styleDisplayTypes.default.toLowerCase()]?.textAttribute) || defaultAttributes.length > 0);

    extendedLayer.sortProperties = extendedLayer.informationAttributes?.filter(
        attr => attr.sort === SORT_ORDER.ASCENDING || attr.sort === SORT_ORDER.DESCENDING
    ).map(attr => ({ sortProperty: attr.attribute, sortOrder: attr.sort }));

    return extendedLayer;
};



// Get stored labels from usermap
function getLabelsFromUsermap(store, layer, layerType, extendedLayer) {
    const userMap = store.getters.getActiveUserMap;

    const customId = layerType === 'feedback' ? layer.layerData.id : layer.layerData.customId;
    const labels = userMap?.content?.sortedLayers?.find(l => l.id === customId)?.labels;
    // If there are labels in de usermap, add the info to the informationAttributes
    if (labels) {
        // reset all current labels in informationattributes
        extendedLayer.informationAttributes.forEach((i) => {
            i.isTextAttribute = false;
        });
        // Set only those as label that are in labels from usermap
        labels.forEach((label) => {
            let informationAttribute = extendedLayer.informationAttributes.find(i => i.id === label.id);
            if (informationAttribute) {
                informationAttribute.isTextAttribute = true;
                informationAttribute.textAttributeSeparator = label.textAttributeSeparator;
                informationAttribute.textAttributeShowLabel = label.textAttributeShowLabel;
            }
        });
    }
}

export const getLabels = function (informationAttributes) {
    return informationAttributes.filter(i => i.isTextAttribute).map(({
        id,
        attribute,
        alias,
        textAttributeShowLabel,
        textAttributeLabel,
        textAttributeSeparator,
        textAttributeOrder
    }) => {
        return {
            id,
            attribute,
            alias,
            textAttributeShowLabel,
            textAttributeLabel,
            textAttributeSeparator,
            textAttributeOrder
        };
    });
};

export const createLayer = async function (store, layerData, extent, filterList, highQuality = false, alwaysCreateWfsLayer = false) {
    switch (layerData.serviceType.toUpperCase()) {
        case 'WMS':
            return createWmsLayer(store, layerData, extent, highQuality);

        case 'WMTS':
            return createWmtsLayer(layerData, highQuality);

        case 'WFS':
            return createWfsLayer(store, layerData, extent, filterList, highQuality, alwaysCreateWfsLayer);

        case 'OGCT':
            return createOgctLayer(store, layerData);

        default:
            return null;
    }
};

export function nonGeoServerFilterLimitationFix(layerData, filters) {
    if (layerData.source.uri_WFS && layerData.source.uri_WFS.toLowerCase().indexOf('wijkenbuurt') !== -1) {
        // fix for #768 Mobile_new: Filteren - Objecten na filteren "bevat" niet selecteerbaar
        return filters.map(filter => ({ settings: { ...filter.settings, omitCase: true } }));
    }
    return filters;
}

export const getRequestUrl = function (layerData, serviceType) {
    if (!serviceType) {
        serviceType = layerData.serviceType;
    }
    // Go directly to GeoServer ( or other OGC web service)
    if (useOgcWebServiceDirectly(layerData)) {
        // PBI 13931: Dashboard: Ondersteunen OWS met aparte URL's voor WMS, WMTS en WFS
        // Make difference between WNS and WFS request Url
        return GetServiceUrl(serviceType, layerData);
    } else {
        // use layers endpoint from openapi spec
        return layerData.customEndPoints[serviceType.toLocaleLowerCase()].url;
    }
};
